import { useState, memo, useRef, HTMLAttributes } from 'react'
import Link from 'next/link'
import Image from 'next/legacy/image'
import cn from 'classnames'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useQuery } from '@apollo/client/react'
// Components
import Loader from '../newLoader/Loader'
import Button from '../newButton/Button'
import styles from './Explore.module.scss'
// Utils
import { getBreedImage, pickBreeds } from './lib/utils'
import { BREED_LIST_EXPLORER } from '@graphql'
import { Breed, IBreedListExplorer } from '@types'
import { useMediaQuery } from '@utils'

interface IBreedCard extends HTMLAttributes<HTMLDivElement> {
  breed: Breed
}

const BreedCard = memo(({ breed, className }: IBreedCard) => (
  <div className={cn(styles.breed, styles.withDogs, className)}>
    <Link
      href={`/search?breed=${breed?.slug}`}
      prefetch={false}
      aria-label={`Explore ${breed?.name} puppies for sale`}
    >
      <Image
        src={
          getBreedImage(breed)
            ? `${process.env.NEXT_PUBLIC_MEDIA_BUCKET}${getBreedImage(breed)}`
            : '/placeholder-breed.svg'
        }
        role="img"
        aria-label={`${breed?.name} sitting and posing`}
        alt={`${breed?.name} sitting and posing`}
        width={165}
        height={165}
        loading="lazy"
        layout="responsive"
      />
      <p>{breed?.name} </p>
    </Link>
  </div>
))

interface IExplore extends HTMLAttributes<HTMLDivElement> {
  breeds: Breed[]
  title: string
  id?: string
}

export default function Explore({ breeds: _breeds, title, className, id }: IExplore) {
  const isMobile = useMediaQuery('(max-width:600px)')
  const limit = 12

  const [breeds, setBreeds] = useState<Breed[]>(
    _breeds.sort((a, b) => b.dogsAvailable - a.dogsAvailable)
  )
  const [offset, setOffset] = useState(0)
  const [breedsShown, setBreedsShown] = useState(12)

  const { loading, refetch } = useQuery<IBreedListExplorer>(BREED_LIST_EXPLORER, {
    variables: { limit, offset },
  })

  const handleViewMore = async () => {
    const newOffset = offset + 1
    setOffset(newOffset)
    setBreedsShown((curr) => curr + limit)
    const { data: newData } = await refetch({ limit, offset: newOffset })
    if (newData?.breedsList?.length) {
      setBreeds((curr) => [...curr, ...newData.breedsList])
    }
  }

  const handleViewLess = () => {
    if (breedsShown > limit) {
      setBreedsShown((curr) => curr - limit)
    }
  }

  /* Swiper Handlers   */

  const ref = useRef(null)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  const goNext = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slideNext()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
      handleViewMore()
    }
  }

  const goPrev = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slidePrev()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
    }
  }

  return (
    <section className={className}>
      <div className={styles.explore} id={id}>
        <div className={styles.header}>
          <h2>{title}</h2>
          <a href={`${process.env.NEXT_PUBLIC_ROOT_URL}/puppies-for-sale#breed`}>View All</a>
        </div>

        <>
          {/* desktop */}
          <div className={cn(styles.breeds, styles.desktop)}>
            {breeds?.slice(0, breedsShown).map((b, i) => (
              <BreedCard key={i} breed={b} />
            ))}
          </div>

          {loading && !isMobile && (
            <div className={styles.loader}>
              <Loader theme={['tall']} />
            </div>
          )}

          {/* mobile */}
          <div className={cn(styles.breeds, styles.mobile, styles.searchMobile)}>
            <button
              className={cn({
                [styles.swiperButton]: true,
                [styles.swiperPrev]: !isBeginning,
                [styles.disabledPrev]: isBeginning,
              })}
              onClick={goPrev}
              role="img"
              aria-label="Button Prev"
            />
            <button
              className={cn({
                [styles.swiperButton]: true,
                [styles.swiperNext]: !isEnd,
                [styles.disabledNext]: isEnd,
              })}
              onClick={goNext}
              role="img"
              aria-label="Button Next"
            />
            <Swiper
              speed={1000}
              spaceBetween={20}
              initialSlide={0}
              grabCursor
              slidesPerView={3}
              ref={ref}
            >
              {pickBreeds(breeds).map((pair, i) => (
                <SwiperSlide key={i}>
                  <div className={styles.pair}>
                    <BreedCard breed={pair[0]} />
                    <BreedCard breed={pair[1]} />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </>

        <div className={styles.buttonContainer}>
          {breedsShown > 12 && (
            <Button
              title="View less"
              theme={['black', 'medium']}
              rightImage="/search/arrow-top-black.svg"
              width={10}
              height={10}
              onClick={handleViewLess}
              altImg="Black up arrow"
            />
          )}
          {breedsShown >= 12 && (
            <Button
              title="View more"
              theme={['black', 'medium']}
              rightImage="/search/arrow-down-black.svg"
              width={10}
              height={10}
              onClick={handleViewMore}
              altImg="Black down arrow"
            />
          )}
        </div>
      </div>
    </section>
  )
}
